// Stat tile: one headline number, its label, an optional comparison line and band. Not a chart,
// so no hover layer; text wears text tokens, the band carries icon + label. DTP's `.stat` look
// (design spec §6.6): hairline card, 13 px label row, 28 px tabular value, 12 px detail.
import type * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING } from "../components/focus-ring";
import { type Band, BandBadge } from "./band";

export type StatTileProps = {
  label: string;
  value: React.ReactNode;
  /** Secondary line, e.g. "target 40" or "+12% vs last week". */
  detail?: React.ReactNode;
  band?: Band;
  /** Decorative 18 px icon in the label row; the band takes its place when both are given. */
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  /** The whole tile becomes a link only when it says so (design spec §6.6); no hover fill. */
  href?: string;
  /**
   * `sm` (Chart UX pass 2026-10-21): the KPI strip of a finance screen, Mosaic / Runway sized:
   * 12 px label, 20 px value, 12 px detail, 12 × 16 padding, so four fit above a table without
   * taking the screen.
   */
  size?: "default" | "sm";
  className?: string;
};

export function StatTile({
  label,
  value,
  detail,
  band,
  icon: Icon,
  href,
  size = "default",
  className,
}: StatTileProps) {
  const Root = href ? "a" : "div";
  const small = size === "sm";
  return (
    <Root
      data-slot="stat-tile"
      data-size={small ? "sm" : undefined}
      {...(href ? { href } : {})}
      className={cn(
        "flex min-w-0 flex-col rounded-lg border border-border bg-card text-card-foreground",
        small ? "px-4 py-3 max-md:px-3" : "px-5 py-[18px] max-md:p-4",
        href && FOCUS_RING,
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-x-2 gap-y-1",
          small ? "min-h-5" : "min-h-6",
        )}
      >
        <span
          className={cn(
            "max-w-full shrink-0 truncate text-muted-foreground",
            small ? "text-xs" : "text-sm",
          )}
        >
          {label}
        </span>
        {band ? (
          <BandBadge band={band} />
        ) : Icon ? (
          <Icon
            className={cn("shrink-0 text-faint max-md:hidden", small ? "size-4" : "size-[18px]")}
            aria-hidden={true}
          />
        ) : null}
      </div>
      <span
        className={cn(
          "font-heading font-semibold tabular-nums",
          small
            ? "mt-0.5 text-xl leading-7 tracking-[-0.5px]"
            : "mt-2 mb-1 text-2xl tracking-[-1px] max-md:text-xl",
        )}
      >
        {value}
      </span>
      {detail ? (
        <span className={cn("text-xs text-muted-foreground", small && "truncate")}>{detail}</span>
      ) : null}
    </Root>
  );
}
